import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Star, MapPin, Clock, Users } from 'lucide-react';
import { Badge } from '../ui/badge';

const RANK_COLORS = {
  pro: "#4A90D9",
  elite: "#9B59B6",
  master: "#F1C40F",
  grandmaster: "#E74C3C",
  legendary: "#E07A5F"
};

const RANK_BADGES = {
  pro: "⭐",
  elite: "⭐⭐",
  master: "⭐⭐⭐",
  grandmaster: "⭐⭐⭐⭐",
  legendary: "👑"
};

export const ProviderCard = ({ provider }) => {
  const { t, language } = useLanguage();
  const defaultImage = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400';
  
  const getCategoryName = (category) => {
    return t(`categories.${category}`) || category;
  };
  
  const getRankName = (rank) => {
    return t(`ranks.${rank}`) || rank;
  };
  
  return (
    <Link 
      to={`/providers/${provider.provider_id}`}
      className="chef-card group block aspect-[4/5] hover-lift"
      data-testid={`provider-card-${provider.provider_id}`}
    >
      {/* Image */}
      <img
        src={provider.profile_image || defaultImage}
        alt={provider.name}
        className="chef-image absolute inset-0 w-full h-full object-cover"
        onError={(e) => {
          e.target.src = defaultImage;
        }}
      />
      
      {/* Overlay */}
      <div className="chef-overlay" />
      
      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-between p-5 text-white z-10">
        {/* Top badges */}
        <div className="flex items-start justify-between">
          {/* Rank badge */}
          <div 
            className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1"
            style={{ backgroundColor: RANK_COLORS[provider.rank] || RANK_COLORS.pro }}
          >
            <span>{RANK_BADGES[provider.rank]}</span>
            <span>{getRankName(provider.rank)}</span>
          </div>
          
          {/* Rating badge */}
          <div className="flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            <span className="text-sm font-medium">{provider.rating.toFixed(1)}</span>
          </div>
        </div>
        
        {/* Bottom content */}
        <div className="space-y-3">
          <div>
            <Badge 
              variant="secondary" 
              className="bg-white/20 text-white text-xs border-0 backdrop-blur-sm mb-2"
            >
              {getCategoryName(provider.service_category)}
            </Badge>
            <h3 className="font-heading text-xl font-semibold">{provider.name}</h3>
            <div className="flex items-center gap-1 text-white/70 text-sm mt-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>{provider.location}</span>
            </div>
          </div>
          
          {/* Specialties */}
          {provider.specialties?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {provider.specialties.slice(0, 2).map((specialty, index) => (
                <Badge 
                  key={index} 
                  variant="secondary" 
                  className="bg-white/10 text-white/90 text-xs border-0"
                >
                  {specialty}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Price & info */}
          <div className="flex items-center justify-between pt-2 border-t border-white/20">
            <div className="flex items-center gap-3 text-sm text-white/70">
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {t('provider.minHours')} {provider.min_hours}h
              </span>
            </div>
            <div className="text-right">
              {provider.pricing_type === 'per_person' && provider.per_person_rate ? (
                <>
                  <span className="text-lg font-semibold">{provider.per_person_rate.toFixed(2)}€</span>
                  <span className="text-white/70 text-sm">/{t('provider.perPerson')}</span>
                </>
              ) : (
                <>
                  <span className="text-lg font-semibold">{(provider.client_hourly_rate || provider.hourly_rate).toFixed(2)}€</span>
                  <span className="text-white/70 text-sm">{t('provider.perHour')}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProviderCard;
